import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorB2ByHealthTeamData } from "@/services/esus/indicator/esb/b2/getResumeIndicatorB2ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEsbEnum } from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEsbB2HealthTeamsFilters } from "./IndicatorEsbB2HealthTeamsFilters";
import { IndicatorEsbB2HealthTeamsClassificationTable } from "./IndicatorEsbB2HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB2HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorB2ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorB2ByHealthTeam: () => Promise<ResumeIndicatorB2ByHealthTeamData[]>;
};

export function IndicatorEsbB2HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorB2ByHealthTeam,
}: Readonly<IndicatorEsbB2HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorB2ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorB2AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Atendimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB2AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorB2ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe ESB": healthTeamClassification.esbHealthTeamName,
					"Equipe ESB INE": healthTeamClassification.esbHealthTeamIne,
					"Equipe ESF": healthTeamClassification.linkedHealthTeamName,
					"Equipe ESF INE": healthTeamClassification.linkedHealthTeamIne,
					"Cidadãos vinculados": healthTeamClassification.totalCitizens,
					"Cidadãos vinculados com tratamento concluído":
						healthTeamClassification.totalCitizensFinishedLinked,
					"Cidadãos não vinculados com tratamento concluído":
						(healthTeamClassification.totalCitizensFinished ?? 0) -
						(healthTeamClassification.totalCitizensFinishedLinked ?? 0),
					"Cidadãos com tratamento concluído":
						healthTeamClassification.totalCitizensFinished,
					"Cidadãos vinculados com consulta programada":
						healthTeamClassification.totalCitizensStartedLinked,
					"Cidadãos não vinculados com consulta programada":
						(healthTeamClassification.totalCitizensStarted ?? 0) -
						(healthTeamClassification.totalCitizensStartedLinked ?? 0),
					"Cidadãos com consulta programada":
						healthTeamClassification.totalCitizensStarted,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalCitizensFinished,
						healthTeamClassification.totalCitizensStarted
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsbEnum.B2,
						calculatePercentage(
							healthTeamClassification.totalCitizensFinished,
							healthTeamClassification.totalCitizensStarted
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB2HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB2HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
